import { Component, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AuthFacade } from 'src/app/auth/auth.facade';
import { IdpConfigId } from 'src/app/auth/models/idp-config-id.enum';
import { AppConfigService } from 'src/app/services/app-config.service';

/**
 * Full-page error screen used for all authentication / access failure scenarios.
 *
 * The `scenario` route data property controls which message pair is shown:
 *  - 'relay'                        → login flow failure (IdP returned error during /relay)
 *  - 'session'                      → session/token expired or renewal failed after login
 *  - 'etm-account-inactive'         → user's ETM account contract is inactive
 *  - 'product-entitlement-inactive' → account is active but the Portfolio product is not enabled
 */
@Component({
  selector: 'app-auth-error',
  standalone: true,
  templateUrl: './auth-error.component.html',
  styleUrls: ['./auth-error.component.scss'],
})
export class AuthErrorComponent {
  private readonly appConfigService = inject(AppConfigService);
  private readonly authFacade = inject(AuthFacade);
  private readonly route = inject(ActivatedRoute);

  private readonly scenario = this.route.snapshot.data['scenario'] as
    | 'relay'
    | 'session'
    | 'etm-account-inactive'
    | 'product-entitlement-inactive';

  readonly isSessionError = this.scenario === 'session';
  readonly isEtmAccountInactive = this.scenario === 'etm-account-inactive';
  readonly isProductEntitlementInactive = this.scenario === 'product-entitlement-inactive';

  goToCommerce() {
    // Resolve the login URL from the active IdP *before* clearing tokens.
    const activeId = this.authFacade.activeConfigId();
    const loginUrl =
      activeId === IdpConfigId.Okta
        ? this.appConfigService.config.okta?.login_url
        : (this.appConfigService.config.oidc?.login_url ??
          this.appConfigService.config.okta?.login_url);

    this.authFacade.logoutLocal();
    window.open(loginUrl, '_self');
  }
}
